import { LaneLine, TargetSource, TrackedTarget } from "@app/shared";
import { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { DataViewerProps } from "../DataViewer";
import { getRadarFrame, getVideoFrame } from "../ProjectWithData";
import { TargetResult } from "./TargetResult";


export const ResultsViewer: React.FC<DataViewerProps> = ({ onFrameChange, ...props }) => {
    const [currentFrame, setCurrentFrame] = useState({ radar: 0, video: 0 });
    const [currentTargetIndex, setCurrentTargetIndex] = useState<number>();

    // Refs for scrolling
    const listRef = useRef<FixedSizeList>(null);
    const outerListRef = useRef<HTMLDivElement>(null);

    //Get the current frame number based on the current time
    useEffect(() => {
        const radarFrame = getRadarFrame(props.project, props.currentTimeS);
        const videoFrame = getVideoFrame(props.project, props.currentTimeS);
        const projectFrame = props.project.projectFrameType === "video" ? videoFrame : radarFrame;
        setCurrentFrame({ radar: radarFrame, video: videoFrame });
        //Set the target of the ID we should be scrolling to
        const lastTargetIndex = props.project.simTargetsByFrame?.[projectFrame]?.[0]?.listIndex;
        if (lastTargetIndex !== undefined && lastTargetIndex !== currentTargetIndex) {
            setCurrentTargetIndex(lastTargetIndex);
        }
    }, [props.currentTimeS, props.project]); // eslint-disable-line react-hooks/exhaustive-deps

    //When our radarID changes, scroll to the target
    useEffect(() => {
        if (currentTargetIndex === undefined) return;

        //listRef.current?.scrollToItem(currentTargetIndex);
        outerListRef.current?.scrollTo({
            top: currentTargetIndex * 180,
            behavior: "smooth",
        });
    }, [currentTargetIndex]);

    const handleTargetOnClick = useCallback((target: TrackedTarget) => {
        setCurrentTargetIndex(target.listIndex);
        var timeInProject = 0;
        if (target.source === TargetSource.Radar) {
            timeInProject = .05 + ((props.project.radarStartDate?.getTime() ?? 0) - (props.project.getDataStartDate()?.getTime() ?? 0)) / 1000 + (target.firstFrameTracked) / (props.project.radarFps ?? 10);
        } else {
            timeInProject = .05 + (target.firstFrameTracked / (props.project.videoFps ?? 10));
        }
        onFrameChange(timeInProject);
    }, [setCurrentTargetIndex, onFrameChange, props.project]);


    // The runtime device object carries its facility (with laneLines) even though the generated
    // Device type doesn't declare it — see LayoutViewer for the same access pattern.
    const facilityLaneLines = useMemo<LaneLine[]>(
        () => (props.project.device as { facility?: { laneLines?: LaneLine[] } } | undefined)?.facility?.laneLines ?? [],
        [props.project.device]
    );

    const TargetRow = useMemo<ComponentType<ListChildComponentProps<FixedSizeList>>>(() => {
        return ({ index, style }) => {
            if ((props.project.simTargets?.length ?? 0) < index) return <></>;
            const target = props.project.simTargets![index];
            const prevTarget = props.project.simTargets?.[index - 1];
            const targetFrame = target.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video;
            const prevTargetFrame = prevTarget?.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video;
            return (
                <div
                    style={style}
                    key={target.targetId}
                >
                    <div className={(prevTarget?.lastFrameTracked ?? 0) < prevTargetFrame && target.firstFrameTracked > targetFrame ? "bg-background-bright-blue" : ""} style={{ height: 5 }}></div>
                    <TargetResult key={target.targetId} target={target} currentFrame={currentFrame} onClick={handleTargetOnClick} laneLines={facilityLaneLines} />
                </div>
            );
        };
    }, [props.project.simTargets, currentFrame, handleTargetOnClick, facilityLaneLines]);

    return (
        <div
            style={{
                minWidth: 600,
                maxHeight: 700,
                overflowY: "auto",

                padding: 8,
            }}
        >
            <div>
                {(props.project.simTargets?.length ?? 0) < 1 && <>No Simulation Targets Found</>}
                {props.project.simTargets &&

                    <FixedSizeList
                        ref={listRef}
                        outerRef={outerListRef}
                        height={670}
                        itemCount={props.project.simTargets.length}
                        itemSize={180}
                        width="100%"
                    >
                        {TargetRow}
                    </FixedSizeList>
                }
            </div>
        </div>
    );
}